import {
  Badge,
  Col,
  DiffVisualColors,
  Empty,
  Space,
  Text,
  type TextColor,
  getReferenceBorderStyle,
  theme,
} from '@/components';
import {
  type ComponentType,
  type FC,
  Fragment,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  label?: string;
  sourceLabel?: string;
  targetLabel?: string;
  index?: number;
  variant?: 'h3' | 'h4' | 'h5' | 'body';
  color?: string;
  padding?: number;
  isReferenceTarget?: boolean;
  isSpitedSource?: boolean;
  sourceLabelWrapper?: ComponentType<PropsWithChildren>;
  hasValue?: boolean;
  isNotMatchFiscalYearInTargetProject?: boolean;
  hasTargetProjectError?: boolean;
};

export const DiffViewerTitleCols: FC<Props> = ({
  label,
  sourceLabel,
  targetLabel,
  index,
  variant = 'h4',
  color,
  padding = 8,
  isReferenceTarget,
  isSpitedSource,
  sourceLabelWrapper: SourceLabelWrapper = Fragment,
  hasValue = true,
  isNotMatchFiscalYearInTargetProject,
  hasTargetProjectError,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const targetDottedBorderStyle = isReferenceTarget
    ? getReferenceBorderStyle(token.colorInfoBorder)
    : 'none';
  const sourceDottedBorderStyle = isSpitedSource
    ? getReferenceBorderStyle(token.colorInfoBorder)
    : 'none';

  return (
    <>
      <Col
        span={12}
        style={{
          backgroundColor:
            !label && sourceLabel !== targetLabel && !isSpitedSource
              ? DiffVisualColors.add
              : undefined,
          paddingTop: padding,
          paddingBottom: padding,
          borderTop: sourceDottedBorderStyle,
          borderInline: sourceDottedBorderStyle,
        }}
      >
        <SourceLabelWrapper>
          <Space>
            {index !== undefined && (
              <Badge count={index} color={token.colorTextSecondary} />
            )}
            <Text
              text={label ?? sourceLabel}
              variant={variant}
              color={color as TextColor}
            />
          </Space>
        </SourceLabelWrapper>
      </Col>
      <Col
        span={12}
        style={{
          paddingTop: padding,
          paddingBottom: padding,
          borderTop: targetDottedBorderStyle,
          borderInline: targetDottedBorderStyle,
          paddingLeft: hasValue ? padding : 0,
        }}
      >
        {hasValue ? (
          <Space>
            {index !== undefined && (
              <Badge count={index} color={token.colorTextSecondary} />
            )}
            <Text
              text={label ?? targetLabel}
              variant={variant}
              color={color as TextColor}
            />
          </Space>
        ) : (
          <Empty
            description={
              hasTargetProjectError || isNotMatchFiscalYearInTargetProject
                ? t('no-fiscal-year-data')
                : t('savepoints.no-project-savepoints-in-group')
            }
            style={{
              backgroundColor: token.colorFillContent,
              paddingTop: 16,
              paddingBottom: 16,
              margin: 0,
            }}
          />
        )}
      </Col>
    </>
  );
};
